<template>
  <app-page :title="$t('@billingPeriods/pageTitle', { year: startYear })" wide>
    <v-flex shrink slot="actions">
      <v-btn flat icon @click="onClickPrevYear">
        <v-icon dark>arrow_back</v-icon>
      </v-btn>
      <v-btn flat icon @click="onClickNextYear">
        <v-icon dark>arrow_forward</v-icon>
      </v-btn>
    </v-flex>

    <v-layout>
      <v-flex xs12>
        <errors-list :errorMessage="errorMessage" :dismissible="true" />

        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :rows-per-page-items="rowsPerPageItems"
          :total-items="pagination.totalItems"
          :pagination.sync="pagination"
          hide-actions
        >
          <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
          <template slot="items" slot-scope="props">
            <tr :class="{ locked: props.item.editable }">
              <grid-cell
                v-for="(header, index) in headers"
                :key="index"
                :header="header"
                :row="props.item"
                @action="onRowClick"
              ></grid-cell>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <v-dialog v-if="billingDateModal" :value="true" persistent lazy full-width width="420px">
      <v-card>
        <v-card-text class="px-0 py-0 date-time-picker-card-body">
          <date-time-picker v-model="billingDate" :min="minBillingDate" :max="maxBillingDate" />
          <v-divider />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat color="primary" @click="billingDateModal = false" :disabled="modalLoading">
            {{ $t('@billingPeriods/datepicker/cancelButtonLabel') }}
          </v-btn>
          <v-btn flat color="primary" @click="saveSchedule" :loading="modalLoading">
            {{ $t('@billingPeriods/datepicker/okButtonLabel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-if="paperInvoiceDateModal" :value="true" persistent lazy full-width width="420px">
      <v-card>
        <v-card-text class="px-0 py-0 date-time-picker-card-body">
          <date-time-picker
            v-model="paperInvoicingDate"
            :min="minInvoicingDate"
            :max="maxInvoicingDate"
          />
          <v-divider />
        </v-card-text>

        <v-card-actions>
          <v-btn
            flat
            color="primary"
            @click="paperInvoiceDateModal = false"
            :disabled="modalLoading"
          >
            {{ $t('@billingPeriods/datepicker/cancelButtonLabel') }}
          </v-btn>
          <v-spacer></v-spacer>

          <v-btn flat color="primary" @click="saveSchedule" :loading="modalLoading">
            {{ $t('@billingPeriods/datepicker/okButtonLabel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="bgcInvoiceSendingUntilModal"
      :value="true"
      persistent
      lazy
      full-width
      width="420px"
    >
      <v-card>
        <v-card-text class="px-0 py-0 date-time-picker-card-body">
          <date-time-picker
            v-model="bgcInvoiceSendingUntil"
            :min="minBgcInvoiceSendingUntil"
            :max="maxBgcInvoiceSendingUntil"
          />
          <v-divider />
        </v-card-text>

        <v-card-actions>
          <v-btn
            flat
            color="primary"
            @click="bgcInvoiceSendingUntilModal = false"
            :disabled="modalLoading"
          >
            {{ $t('@billingPeriods/datepicker/cancelButtonLabel') }}
          </v-btn>
          <v-spacer></v-spacer>

          <v-btn flat color="primary" @click="saveSchedule" :loading="modalLoading">
            {{ $t('@billingPeriods/datepicker/okButtonLabel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-if="paperInvoiceSendAtModal" :value="true" persistent lazy full-width width="420px">
      <v-card>
        <v-card-text class="px-0 py-0 date-time-picker-card-body">
          <date-time-picker
            v-model="paperInvoiceSendAt"
            :min="minInvoicingDate"
            :max="maxInvoicingDate"
          />
          <v-divider />
        </v-card-text>

        <v-card-actions>
          <v-btn
            flat
            color="primary"
            @click="paperInvoiceSendAtModal = false"
            :disabled="modalLoading"
          >
            {{ $t('@billingPeriods/datepicker/cancelButtonLabel') }}
          </v-btn>
          <v-spacer></v-spacer>

          <v-btn flat color="primary" @click="saveSchedule" :loading="modalLoading">
            {{ $t('@billingPeriods/datepicker/okButtonLabel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </app-page>
</template>

<script>
import moment from 'moment';
import GridCell from '../../components/GridCell';
import ErrorsList from '../../components/ErrorsList';
import { getErrorMessage } from '../../errorHandlers';
import { defaultDateTimeFormatter } from '../../utils';
import DateTimePicker from './DateTimePicker.vue';

export default {
  components: { GridCell, ErrorsList, DateTimePicker },

  data() {
    return {
      billingDate: null,
      billingDateModal: false,

      paperInvoicingDate: null,
      paperInvoiceDateModal: false,

      bgcInvoiceSendingUntil: null,
      bgcInvoiceSendingUntilModal: false,

      paperInvoiceSendAt: null,
      paperInvoiceSendAtModal: false,

      modalLoading: false,
      selectedPeriodId: 0,

      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 15,
        sortBy: 'created',
        totalItems: 0,
      },
      rowsPerPageItems: [10, 15, 50],
      headers: [
        {
          text: this.$t('@billingPeriods/list/yearColumnHeader'),
          align: 'left',
          sortable: false,
          value: 'year',
          width: 64,
        },
        {
          text: this.$t('@billingPeriods/list/monthColumnHeader'),
          value: 'month',
          sortable: false,
          width: 64,
        },
        {
          text: '',
          actions: () => [{ icon: 'event', name: 'billingAt' }],
          sortable: false,
          align: 'right',
          value: '&nbsp;',
          width: 20,
          cellClass: (row) => ({ faded: this.isDatePassed(row.billingAt) }),
        },
        {
          text: this.$t('@billingPeriods/list/billingDateColumnHeader'),
          value: 'billingAt',
          sortable: false,
          format: defaultDateTimeFormatter,
          cellClass: (row) => ({ faded: this.isDatePassed(row.billingAt) }),
        },
        {
          text: '',
          actions: () => [{ icon: 'event', name: 'bgcInvoiceSendingUntil' }],
          sortable: false,
          align: 'right',
          value: '&nbsp;',
          width: 20,
          cellClass: (row) => ({ faded: this.isDatePassed(row.paperInvoicingAt) }),
        },
        {
          text: this.$t('@billingPeriods/list/bgcInvoiceSendingUntilColumnHeader'),
          value: 'bgcInvoiceSendingUntil',
          sortable: false,
          format: defaultDateTimeFormatter,
          cellClass: (row) => ({ faded: this.isDatePassed(row.paperInvoicingAt) }),
        },
        {
          text: '',
          actions: () => [{ icon: 'event', name: 'paperInvoicingAt' }],
          sortable: false,
          align: 'right',
          value: '&nbsp;',
          width: 20,
          cellClass: (row) => ({ faded: this.isDatePassed(row.paperInvoicingAt) }),
        },
        {
          text: this.$t('@billingPeriods/list/paperInvoiceDateColumnHeader'),
          value: 'paperInvoicingAt',
          sortable: false,
          format: defaultDateTimeFormatter,
          cellClass: (row) => ({ faded: this.isDatePassed(row.paperInvoicingAt) }),
        },
        {
          text: '',
          actions: () => [{ icon: 'event', name: 'paperInvoiceSendAt' }],
          sortable: false,
          align: 'right',
          value: '&nbsp;',
          width: 20,
          cellClass: (row) => ({ faded: row.editable }),
        },
        {
          text: this.$t('@billingPeriods/list/paperInvoiceSendAtColumnHeader'),
          value: 'paperInvoiceSendAt',
          sortable: false,
          format: defaultDateTimeFormatter,
          cellClass: (row) => ({ faded: row.editable }),
        },
      ],
      items: [],
      firstMonthOfNextYearSchedule: null,
      loading: false,
      errorMessage: '',
      serverNow: null,
      startYear: new Date().getFullYear(),
      currentMonth: new Date().getMonth() + 1,
    };
  },

  watch: {
    pagination() {
      this.fetchData();
    },

    params() {
      this.fetchData();
    },
  },

  computed: {
    selectedPeriod: {
      get() {
        return this.items.find((v) => v.id === this.selectedPeriodId) || {};
      },

      set(val) {
        // update period item in periods collection, so it is picked up by data table component
        const index = this.items.findIndex((v) => v.id === this.selectedPeriodId);
        if (index >= 0) {
          this.items[index] = {
            ...this.items[index],
            ...val,
          };
        }
      },
    },

    minBillingDate() {
      if (!this.selectedPeriod || !this.selectedPeriod.yearAndMonth) {
        return undefined;
      }

      return moment(this.selectedPeriod.yearAndMonth)
        .startOf('month')
        .toISOString();
    },

    maxBillingDate() {
      if (!this.selectedPeriod || !this.selectedPeriod.paperInvoicingAt) {
        return undefined;
      }
      return moment(this.selectedPeriod.paperInvoicingAt)
        .startOf('day')
        .toISOString();
    },

    minBgcInvoiceSendingUntil() {
      if (!this.selectedPeriod || !this.selectedPeriod.billingAt) {
        return undefined;
      }

      return moment(this.selectedPeriod.billingAt).toISOString();
    },

    maxBgcInvoiceSendingUntil() {
      if (!this.selectedPeriod || !this.selectedPeriod.paperInvoicingAt) {
        return undefined;
      }

      return moment(this.selectedPeriod.paperInvoicingAt).toISOString();
    },

    minInvoicingDate() {
      if (!this.selectedPeriod || !this.selectedPeriod.yearAndMonth) {
        return undefined;
      }
      return moment(this.selectedPeriod.yearAndMonth)
        .add(1, 'months')
        .startOf('month')
        .add(1, 'days')
        .toISOString();
    },

    maxInvoicingDate() {
      if (!this.selectedPeriod || !this.selectedPeriod.month) {
        return undefined;
      }

      const nextMonth = this.selectedPeriod.month + 1;
      const nextPeriod =
        nextMonth === 13
          ? this.firstMonthOfNextYearSchedule
          : this.items.find((item) => item.month === nextMonth);

      if (!nextPeriod || !nextPeriod.billingAt) {
        return undefined;
      }

      return moment(nextPeriod.billingAt)
        .startOf('day')
        .toISOString();
    },
  },

  methods: {
    onRowClick({ action, row }) {
      if (action && action.icon === 'event') {
        this.selectedPeriodId = row.id;

        this.billingDate = moment(this.selectedPeriod.billingAt).toISOString();
        this.bgcInvoiceSendingUntil = moment(
          this.selectedPeriod.bgcInvoiceSendingUntil,
        ).toISOString();
        this.paperInvoicingDate = moment(this.selectedPeriod.paperInvoicingAt).toISOString();
        this.paperInvoiceSendAt = moment(this.selectedPeriod.paperInvoiceSendAt).toISOString();

        if (action.name === 'billingAt') {
          this.billingDateModal = true;
        } else if (action.name === 'bgcInvoiceSendingUntil') {
          this.bgcInvoiceSendingUntilModal = true;
        } else if (action.name === 'paperInvoicingAt') {
          this.paperInvoiceDateModal = true;
        } else if (action.name === 'paperInvoiceSendAt') {
          this.paperInvoiceSendAtModal = true;
        }
      }
    },

    async saveSchedule() {
      const billingAt = moment(this.billingDate).toISOString();
      const bgcInvoiceSendingUntil = moment(this.bgcInvoiceSendingUntil).toISOString();
      const paperInvoicingAt = moment(this.paperInvoicingDate).toISOString();
      const paperInvoiceSendAt = moment(this.paperInvoiceSendAt).toISOString();

      const payload = {
        ...this.selectedPeriod,
        billingAt,
        bgcInvoiceSendingUntil,
        paperInvoicingAt,
        paperInvoiceSendAt,
      };

      this.modalLoading = true;
      try {
        await this.$http.put(`/api/debts/schedule`, payload);
        this.selectedPeriod = payload;
        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.billingDateModal = false;
        this.bgcInvoiceSendingUntilModal = false;
        this.paperInvoiceDateModal = false;
        this.paperInvoiceSendAtModal = false;
        this.modalLoading = false;
        this.fetchData();
      }
    },

    isDatePassed(date) {
      return this.serverNow && this.serverNow.isAfter(date);
    },

    onClickPrevYear() {
      this.startYear = moment(this.startYear, 'YYYY')
        .subtract(1, 'years')
        .format('YYYY');
      this.fetchData();
    },

    onClickNextYear() {
      this.startYear = moment(this.startYear, 'YYYY')
        .add('years', 1)
        .format('YYYY');
      this.fetchData();
    },

    async fetchData() {
      try {
        this.loading = true;
        // Fetch data for selected year + next year to be able to calculate paper invoicing constraints for December
        const tillYear = Number(this.startYear) + 1;
        const url = `/api/debts/schedule?fromYear=${this.startYear}&tillYear=${tillYear}`;
        const {
          data: { data, now: nowIsoString },
        } = await this.$http.get(url);

        // Initial page load will point at future billing periods
        // Allowing to navigate to past and future, time-machine service friendly
        const serverNow = moment(nowIsoString);
        this.serverNow = serverNow;
        const serverYear = serverNow.format('YYYY');
        if (this.startYear < serverYear && !this.items.length) {
          this.loading = false;
          this.startYear = serverYear;
          return this.fetchData();
        }

        this.items = data.slice(0, 12).map((item) => ({
          ...item,
          editable: [item.billingAt, item.paperInvoicingAt].every((v) => serverNow.isAfter(v)),
        }));
        this.pagination.totalItems = data.length;
        this.firstMonthOfNextYearSchedule = data[12];
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },
  },

  created() {
    this.params = this.$router.currentRoute.query;
  },

  beforeRouteUpdate(to, from, next) {
    this.params = to.query;
    next();
  },
};
</script>
<style scoped>
>>> .v-datatable th:nth-child(2),
>>> .v-datatable th:nth-child(4),
>>> .v-datatable th:nth-child(6),
>>> .v-datatable th:nth-child(8),
>>> .v-datatable th:nth-child(10),
>>> .v-datatable td:nth-child(2),
>>> .v-datatable td:nth-child(4),
>>> .v-datatable td:nth-child(6),
>>> .v-datatable td:nth-child(8),
>>> .v-datatable td:nth-child(10) {
  padding-left: 0px;
}
tr.locked > td {
  pointer-events: none;
}
.faded {
  opacity: 0.5;
}
</style>
